import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
  Cell
} from "recharts";

// 日期轉換為季度格式
const formatToQuarter = (dateStr) => {
  if (!dateStr) return '';
  const [year, month] = dateStr.split('-');
  const monthNum = parseInt(month);
  let quarter;
  if (monthNum >= 1 && monthNum <= 3) quarter = 'Q1';
  else if (monthNum >= 4 && monthNum <= 6) quarter = 'Q2';
  else if (monthNum >= 7 && monthNum <= 9) quarter = 'Q3';
  else quarter = 'Q4';
  return `${year} ${quarter}`;
};

// 年度聚合函数 - 優先取第四季度，否則累加各季度除以4的值
const aggregateYearly = (data, metric) => {
  const yearly = {};
  
  data.forEach(({ date, value }) => {
    if (!date || value === undefined || value === null) return;
    const year = date.slice(0, 4);
    const month = date.slice(5, 7);
    if (!yearly[year]) yearly[year] = { quarters: [], hasQ4: false };
    
    const parsedValue = parseFloat(value) || 0;
    yearly[year].quarters.push(parsedValue);
    
    // 檢查是否為第四季度（10月、11月或12月）
    if (month === '10' || month === '11' || month === '12') {
      yearly[year].hasQ4 = true;
      yearly[year].q4Value = parsedValue;
    }
  });

  return Object.entries(yearly).map(([year, { quarters, hasQ4, q4Value }]) => {
    let yearValue;
    if (hasQ4) {
      // 如果有第四季度數據，使用第四季度（已是年化數據）
      yearValue = q4Value;
    } else {
      // 否則將每個季度除以4後相加
      yearValue = quarters.reduce((sum, q) => sum + q / 4, 0);
    }
    return { year, [metric]: yearValue };
  });
};

// 构建季度图表数据（PCEC + GPDI + GCE + EXPGS - IMPGS = GDP）
const buildQuarterlyChartData = (filteredDates, data) => {
  const dateMap = {};
  
  // 按日期分组数据
  data.forEach(({ date, name, value, metric_name, metric_symbol }) => {
    if (!date) return;
    
    const key = date.slice(0, 7); // YYYY-MM 格式
    if (!dateMap[key]) {
      dateMap[key] = { date: key, displayDate: formatToQuarter(date) };
    }
    
    // 处理不同的指标名称格式 - 优先使用 name 字段
    const symbol = name || metric_symbol;
    const parsedValue = parseFloat(value) || 0;
    
    // 季度數據需要除以4（因為是年化數據）
    const quarterValue = parsedValue / 4;
    
    // 直接使用标识符映射
    if (symbol === 'PCEC') {
      dateMap[key].PCEC = quarterValue;
    } else if (symbol === 'GPDI') {
      dateMap[key].GPDI = quarterValue;
    } else if (symbol === 'GCE') {
      dateMap[key].GCE = quarterValue;
    } else if (symbol === 'EXPGS') {
      dateMap[key].EXPGS = quarterValue;
    } else if (symbol === 'IMPGS') {
      dateMap[key].IMPGS = quarterValue;
    } else if (symbol === 'GDP') {
      dateMap[key].GDP = quarterValue;
    } else if (symbol === 'GDPC1') {
      dateMap[key].GDPC1 = quarterValue;
    }
  });
  
  // 转换为数组
  const result = Object.values(dateMap)
    .map(item => {
      // 确保有完整的数据
      const PCEC = item.PCEC || 0;
      const GPDI = item.GPDI || 0;
      const GCE = item.GCE || 0;
      const EXPGS = item.EXPGS || 0;
      const IMPGS = item.IMPGS || 0;
      const GDPC1 = item.GDPC1 || 0;
      
      // 计算淨出口
      const NETEXP = EXPGS - IMPGS;
      
      // 计算名目 GDP
      const calculatedGDP = PCEC + GPDI + GCE + NETEXP;
      const GDP = item.GDP || calculatedGDP;
      
      return {
        date: item.date,
        displayDate: item.displayDate,
        PCEC,
        GPDI,
        GCE,
        EXPGS,
        IMPGS,
        NETEXP,
        GDP,
        GDPC1
      };
    })
    .filter(item => {
      // 只要有任何一个值不为0就保留
      return item.PCEC !== 0 || item.GPDI !== 0 || item.GCE !== 0 || item.EXPGS !== 0 || item.IMPGS !== 0;
    })
    .sort((a, b) => a.date.localeCompare(b.date));
  
  return result;
};

// 构建年度图表数据（名目 GDP vs 实质 GDP）
const buildYearlyChartData = (data) => {
  
  // 分别聚合名目 GDP 和实质 GDP - 简化判断逻辑
  const nominalGDP = data.filter(d => {
    const symbol = d.name || d.metric_symbol;
    return symbol === 'GDP';
  });
  
  const realGDP = data.filter(d => {
    const symbol = d.name || d.metric_symbol;
    return symbol === 'GDPC1';
  });
  
  const nominalYearly = aggregateYearly(nominalGDP, 'GDP_NOMINAL');
  const realYearly = aggregateYearly(realGDP, 'GDP_REAL');
  
  // 合并数据
  const yearMap = new Map();
  nominalYearly.forEach(({ year, GDP_NOMINAL }) => {
    yearMap.set(year, { year, GDP_NOMINAL });
  });
  realYearly.forEach(({ year, GDP_REAL }) => {
    const existing = yearMap.get(year) || { year };
    existing.GDP_REAL = GDP_REAL;
    yearMap.set(year, existing);
  });
  
  const result = Array.from(yearMap.values())
    .filter(d => d.GDP_NOMINAL || d.GDP_REAL)
    .sort((a, b) => a.year.localeCompare(b.year));
  
  return result;
};

// 自定义 Tooltip
const CustomTooltip = ({ active, payload, label, isYearly }) => {
  if (!active || !payload || payload.length === 0) return null;
  
  if (isYearly) {
    // 年度模式：显示名目 GDP 和实质 GDP
    const data = payload[0].payload;
    return (
      <div style={{
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        border: '1px solid #ccc',
        borderRadius: '8px',
        padding: '12px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
      }}>
        <p style={{ margin: '0 0 8px 0', fontWeight: 'bold', fontSize: '14px' }}>
          {label} 年
        </p>
        <div style={{ fontSize: '13px' }}>
          {data.GDP_NOMINAL && (
            <p style={{ margin: '4px 0', color: '#ff6b6b' }}>
              <strong>名目GDP:</strong> ${(data.GDP_NOMINAL / 1000).toFixed(2)}T
            </p>
          )}
          {data.GDP_REAL && (
            <p style={{ margin: '4px 0', color: '#51cf66' }}>
              <strong>實質GDP:</strong> ${(data.GDP_REAL / 1000).toFixed(2)}T
            </p>
          )}
          {data.GDP_NOMINAL && data.GDP_REAL && (
            <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#666', borderTop: '1px solid #ddd', paddingTop: '6px' }}>
              <strong>差距:</strong> ${((data.GDP_NOMINAL - data.GDP_REAL) / 1000).toFixed(2)}T 
              ({(((data.GDP_NOMINAL - data.GDP_REAL) / data.GDP_REAL * 100)).toFixed(1)}%)
            </p>
          )}
        </div>
      </div>
    );
  } else {
    // 季度模式：显示 PCEC, GPDI, GCE, EXPGS, IMPGS 组成
    const data = payload[0].payload;
    return (
      <div style={{
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        border: '1px solid #ccc',
        borderRadius: '8px',
        padding: '12px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
      }}>
        <p style={{ margin: '0 0 8px 0', fontWeight: 'bold', fontSize: '14px' }}>
          {data.displayDate || label}
        </p>
        <div style={{ fontSize: '13px' }}>
          <p style={{ margin: '4px 0', color: '#4dabf7' }}>
            <strong>個人消費支出:</strong> ${(data.PCEC / 1000).toFixed(2)}T
          </p>
          <p style={{ margin: '4px 0', color: '#a469db' }}>
            <strong>私人投資:</strong> ${(data.GPDI / 1000).toFixed(2)}T
          </p>
          <p style={{ margin: '4px 0', color: '#5f5f5f' }}>
            <strong>政府支出:</strong> ${(data.GCE / 1000).toFixed(2)}T
          </p>
          <p style={{ margin: '4px 0', color: '#51cf66' }}>
            <strong>出口總額:</strong> ${(data.EXPGS / 1000).toFixed(2)}T
          </p>
          <p style={{ margin: '4px 0', color: '#ff6b6b' }}>
            <strong>進口總額:</strong> ${(data.IMPGS / 1000).toFixed(2)}T
          </p>
          <p style={{ margin: '4px 0', color: '#ffa94d', fontSize: '12px' }}>
            <strong>淨出口:</strong> ${(data.NETEXP / 1000).toFixed(2)}T
          </p>
          <p style={{ 
            margin: '8px 0 0 0', 
            fontSize: '12px', 
            fontWeight: 'bold',
            borderTop: '1px solid #ddd', 
            paddingTop: '6px' 
          }}>
            <strong>名目GDP:</strong> ${(data.GDP / 1000).toFixed(2)}T
          </p>
          {data.GDPC1 > 0 && (
            <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#51cf66' }}>
              <strong>實質GDP:</strong> ${(data.GDPC1 / 1000).toFixed(2)}T
            </p>
          )}
        </div>
      </div>
    );
  }
};

const GDPChart = ({ filteredDates, data, range }) => {
  // 判断是否为年度聚合模式
  const isYearly = range > 365 * 20 + 31; // 20年以上
  
  const chartData = isYearly 
    ? buildYearlyChartData(data)
    : buildQuarterlyChartData(filteredDates, data);
  
  if (!chartData || chartData.length === 0) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: 400,
        fontSize: '16px',
        color: '#666'
      }}>
        暫無GDP數據
      </div>
    );
  }

  if (isYearly) {
    // 年度模式：并列显示名目 GDP 和实质 GDP
    return (
      <ResponsiveContainer width="100%" height={600}>
        <BarChart
          data={chartData}
          margin={{ top: 30, right: 80, left: 80, bottom: 50 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="year"
            angle={-45}
            textAnchor="end"
            tick={{ fontSize: 12 }}
            height={80}
            label={{
              value: "年份",
              position: "insideBottom",
              offset: -10
            }}
          />
          <YAxis
            tickFormatter={(v) => `$${(v / 1000).toFixed(1)}T`}
            label={{
              value: "GDP (兆美元)",
              angle: -90,
              position: "insideLeft",
              offset: -60,
              style: { textAnchor: 'middle' }
            }}
          />
          <Tooltip content={<CustomTooltip isYearly={true} />} />
          <Legend wrapperStyle={{ paddingTop: '20px' }} />
          
          <Bar
            dataKey="GDP_NOMINAL"
            name="名目GDP"
            fill="#ff6b6b"
            maxBarSize={40}
          />
          <Bar
            dataKey="GDP_REAL"
            name="實質GDP"
            fill="#51cf66"
            maxBarSize={40}
          />
        </BarChart>
      </ResponsiveContainer>
    );
  } else {
    // 季度模式：堆叠显示 PCEC + GPDI + GCE + EXPGS - IMPGS
    return (
      <ResponsiveContainer width="100%" height={600}>
        <BarChart
          data={chartData}
          margin={{ top: 30, right: 80, left: 80, bottom: 50 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="displayDate"
            angle={-45}
            textAnchor="end"
            tick={{ fontSize: 12 }}
            height={80}
            label={{
              value: "季度",
              position: "insideBottom",
              offset: -10
            }}
          />
          <YAxis
            tickFormatter={(v) => `${(v / 1000).toFixed(1)}T`}
            label={{
              value: "GDP組成 (兆美元)",
              angle: -90,
              position: "insideLeft",
              offset: -60,
              style: { textAnchor: 'middle' }
            }}
          />
          <Tooltip content={<CustomTooltip isYearly={false} />} />
          <Legend wrapperStyle={{ paddingTop: '20px' }} />
          
          {/* X 轴基准线 */}
          <ReferenceLine y={0} stroke="#666" strokeWidth={1.5} />
          
          {/* 正值堆叠：PCEC + GPDI + GCE + EXPGS */}
          <Bar
            dataKey="PCEC"
            stackId="positive"
            name="個人消費支出"
            fill="#4dabf7"
          />
          <Bar
            dataKey="GPDI"
            stackId="positive"
            name="私人投資"
            fill="#a469db"
          />
          <Bar
            dataKey="GCE"
            stackId="positive"
            name="政府支出"
            fill="#5f5f5f"
          />
          <Bar
            dataKey="EXPGS"
            stackId="positive"
            name="出口總額"
            fill="#51cf66"
          />
          
          {/* 负值：IMPGS - 显示为负数 */}
          <Bar
            dataKey="IMPGS"
            stackId="positive"
            name="進口總額"
            fill="#ff6b6b"
            // 將進口顯示為負值
            shape={(props) => {
              const { x, y, width, height, value } = props;
              if (!value) return null;
              // 計算負值的位置
              const newY = y + height;
              const newHeight = -height;
              return (
                <rect
                  x={x}
                  y={newY + newHeight}
                  width={width}
                  height={Math.abs(newHeight)}
                  fill="#ff6b6b"
                />
              );
            }}
          />
        </BarChart>
      </ResponsiveContainer>
    );
  }
};

export default GDPChart;